import { useState, useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Clock, History, BarChart2, MessageSquare } from 'lucide-react';
import FlooraLogo from '../common/FlooraLogo';
import FeedbackModal from '../common/FeedbackModal';
import { useAuth } from '../../context/AuthContext';
import { useActiveTask } from '../../hooks/useActiveTask';
import { useCompanySettings } from '../../hooks/useCompanyData';
import { buildLogoUrl } from '../../utils/logo';
import { prefetchAllProductionStats } from '../../hooks/useProductionStats';
import './ProductionLayout.css';

function preloadInsightsPage() {
  import('../../pages/production/ProductionInsightsPage');
}

export default function ProductionLayout() {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { activeTasks } = useActiveTask();
  const { settings } = useCompanySettings();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  useEffect(() => {
    prefetchAllProductionStats();
  }, []);

  const logoUrl = buildLogoUrl(settings?.logoUrl || null, settings?.logoVersion || null);
  const logoSize = settings?.logoSize ?? 36;
  // Cap logo height for the compact topbar (max 34px to fit within 56px topbar).
  const topbarLogoSize = Math.min(logoSize, 34);

  return (
    <div className="production-layout">
      <header className="production-topbar">
        <NavLink to="/production" end className="production-brand">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={t('a11y.companyLogo')}
              style={{
                height: topbarLogoSize,
                maxWidth: 160,
                objectFit: 'contain',
                display: 'block',
              }}
            />
          ) : (
            <FlooraLogo color="brand" height={topbarLogoSize} />
          )}
        </NavLink>
        <div className="production-topbar-right">
          <button
            className="btn btn-icon production-feedback-btn"
            onClick={() => setFeedbackOpen(true)}
            title={t('a11y.shareFeedback')}
            aria-label={t('a11y.shareFeedback')}
          >
            <MessageSquare size={19} />
          </button>
          <NavLink to="/settings/profile" className="production-avatar-link">
            <div className="production-avatar">{profile?.displayName?.[0] ?? '?'}</div>
          </NavLink>
        </div>
      </header>

      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}

      <main className="production-content">
        <Outlet />
      </main>

      <nav className="production-bottomnav">
        <NavLink to="/production/start-task" className={({ isActive }) => `bottomnav-item ${isActive ? 'active' : ''}`}>
          <Plus size={22} />
          <span>{t('nav.startTask')}</span>
        </NavLink>

        <NavLink to="/production/active-task" className={({ isActive }) => `bottomnav-item ${isActive ? 'active' : ''}`}>
          <div className="bottomnav-icon-wrap">
            <Clock size={22} />
            {activeTasks.length > 0 && <span className="bottomnav-live-dot" />}
            {activeTasks.length > 1 && (
              <span className="bottomnav-count">{activeTasks.length}</span>
            )}
          </div>
          <span>{t('nav.activeTask')}</span>
        </NavLink>

        <NavLink to="/production/recent" className={({ isActive }) => `bottomnav-item ${isActive ? 'active' : ''}`}>
          <History size={22} />
          <span>{t('nav.recentTasks')}</span>
        </NavLink>

        <NavLink
          to="/production/insights"
          className={({ isActive }) => `bottomnav-item ${isActive ? 'active' : ''}`}
          onMouseEnter={preloadInsightsPage}
          onFocus={preloadInsightsPage}
          onTouchStart={preloadInsightsPage}
        >
          <BarChart2 size={22} />
          <span>{t('nav.insights')}</span>
        </NavLink>
      </nav>
    </div>
  );
}
